import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText,
  Globe,
  Database,
  Calendar,
  Tag,
  Check,
  XCircle,
  Clock,
  Play
} from 'lucide-react';
import { testCasesApi, testsApi } from '../services/api';
import { TestCase, TestCaseStatus } from '../types';

interface TestCasesProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const TestCases: React.FC<TestCasesProps> = ({ addNotification }) => {
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSourceType, setSelectedSourceType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [approvingTestCases, setApprovingTestCases] = useState<Set<number>>(new Set());
  const [testCaseExecutions, setTestCaseExecutions] = useState<Record<number, number>>({});
  const [formData, setFormData] = useState({
    question_text: '',
    source_type: 'manual' as 'pdf' | 'webpage' | 'csv' | 'manual',
    category: '',
    status: 'PENDING' as TestCaseStatus,
    organization_id: undefined as number | undefined,
    project_id: undefined as number | undefined,
    source_file: undefined as string | undefined,
    source_content: undefined as string | undefined,
    technique_id: undefined as number | undefined,
  });

  const emptyForm = () => ({
    question_text: '',
    source_type: 'manual' as const,
    category: '',
    status: 'PENDING' as TestCaseStatus,
    organization_id: undefined as number | undefined,
    project_id: undefined as number | undefined,
    source_file: undefined as string | undefined,
    source_content: undefined as string | undefined,
    technique_id: undefined as number | undefined,
  });

  const loadTestCases = useCallback(async () => {
    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;

      if (!orgId || !projectId) {
        setTestCases([]);
        return;
      }

      const data = await testCasesApi.list(orgId, projectId, selectedSourceType, selectedCategory, selectedStatus);
      setTestCases(data);
      
      // Load execution counts for each test case (filtered by project)
      const executionsData = await testsApi.listExecutions(orgId, projectId);
      const executionCounts: Record<number, number> = {};
      executionsData.forEach((execution: any) => {
        if (execution.test_case_id) {
          executionCounts[execution.test_case_id] = (executionCounts[execution.test_case_id] || 0) + 1;
        }
      });
      setTestCaseExecutions(executionCounts);
    } catch (error) {
      addNotification('Failed to load test cases', 'error');
      console.error('Error loading test cases:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedSourceType, selectedCategory, selectedStatus, addNotification]);

  useEffect(() => {
    loadTestCases();
    loadCategories();
  }, [loadTestCases]);

  const loadCategories = async () => {
    try {
      const data = await testCasesApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleCreateTestCase = async () => {
    try {
      if (!formData.question_text.trim()) {
        addNotification('Test case text is required', 'error');
        return;
      }

      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const organization_id = orgIdStr ? parseInt(orgIdStr, 10) : NaN;
      const project_id = projectIdStr ? parseInt(projectIdStr, 10) : NaN;
      if (!organization_id || !project_id) {
        addNotification('Select an organization and project in the header first', 'error');
        return;
      }

      await testCasesApi.create({
        ...formData,
        organization_id,
        project_id,
      });
      addNotification('Test case created successfully', 'success');
      setShowCreateModal(false);
      setFormData(emptyForm());
      loadTestCases();
    } catch (error) {
      addNotification('Failed to create test case', 'error');
      console.error('Error creating test case:', error);
    }
  };

  const handleEditTestCase = async () => {
    if (!editingTestCase) return;
    
    try {
      if (!formData.question_text.trim()) {
        addNotification('Test case text is required', 'error');
        return;
      }

      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const organization_id =
        formData.organization_id ??
        editingTestCase.organization_id ??
        (orgIdStr ? parseInt(orgIdStr, 10) : 0);
      const project_id =
        formData.project_id ??
        editingTestCase.project_id ??
        (projectIdStr ? parseInt(projectIdStr, 10) : 0);
      if (!organization_id || !project_id) {
        addNotification('Organization or project is missing; select them in the header', 'error');
        return;
      }

      await testCasesApi.update(editingTestCase.id, {
        question_text: formData.question_text,
        source_type: formData.source_type,
        source_file: formData.source_file ?? editingTestCase.source_file,
        source_content: formData.source_content ?? editingTestCase.source_content,
        category: formData.category,
        technique_id: formData.technique_id ?? editingTestCase.technique_id,
        status: formData.status,
        organization_id,
        project_id,
      });
      addNotification('Test case updated successfully', 'success');
      setShowEditModal(false);
      setEditingTestCase(null);
      setFormData(emptyForm());
      loadTestCases();
    } catch (error) {
      addNotification('Failed to update test case', 'error');
      console.error('Error updating test case:', error);
    }
  };

  const handleEditClick = (testCase: TestCase) => {
    setEditingTestCase(testCase);
    setFormData({
      question_text: testCase.question_text,
      source_type: testCase.source_type,
      category: testCase.category || '',
      status: testCase.status,
      organization_id: testCase.organization_id,
      project_id: testCase.project_id,
      source_file: testCase.source_file,
      source_content: testCase.source_content,
      technique_id: testCase.technique_id,
    });
    setShowEditModal(true);
  };


  const handleDeleteTestCase = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this test case? This action cannot be undone.')) {
      return;
    }

    try {
      await testCasesApi.delete(id);
      addNotification('Test case deleted successfully', 'success');
      loadTestCases();
    } catch (error: any) {
      console.error('Error deleting test case:', error);
      
      // Extract error message from response
      let errorMessage = 'Failed to delete test case';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      addNotification(errorMessage, 'error');
    }
  };

  const getSourceTypeIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'pdf': return <FileText className="h-4 w-4" />;
      case 'webpage': return <Globe className="h-4 w-4" />;
      case 'csv': return <Database className="h-4 w-4" />;
      default: return <Edit className="h-4 w-4" />;
    }
  };

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'pdf': return 'text-blue-600 bg-blue-100';
      case 'webpage': return 'text-green-600 bg-green-100';
      case 'csv': return 'text-purple-600 bg-purple-100';
      default: return 'text-slate-300 bg-slate-600';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle test case approval
  const handleApproveTestCase = async (id: number) => {
    try {
      setApprovingTestCases(prev => new Set(prev).add(id));
      await testCasesApi.approve(id);
      addNotification('Test case approved successfully', 'success');
      loadTestCases();
    } catch (error) {
      addNotification('Failed to approve test case', 'error');
      console.error('Error approving test case:', error);
    } finally {
      setApprovingTestCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Handle test case rejection
  const handleRejectTestCase = async (id: number) => {
    try {
      setApprovingTestCases(prev => new Set(prev).add(id));
      await testCasesApi.reject(id);
      addNotification('Test case rejected successfully', 'success');
      loadTestCases();
    } catch (error) {
      addNotification('Failed to reject test case', 'error');
      console.error('Error rejecting test case:', error);
    } finally {
      setApprovingTestCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Get status badge component
  const getStatusBadge = (status: TestCaseStatus) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'APPROVED':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <Check className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'REJECTED':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case 'PENDING':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  const filteredTestCases = testCases.filter(tc => {
    if (selectedCategory && tc.category !== selectedCategory) return false;
    if (selectedSourceType && tc.source_type !== selectedSourceType) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Test Cases</h1>
            <p className="text-slate-300 mt-2">View and manage existing test cases for your RAG system</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Test Case
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Source Type</label>
            <select
              value={selectedSourceType}
              onChange={(e) => setSelectedSourceType(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Sources</option>
              <option value="pdf">PDF</option>
              <option value="webpage">Webpage</option>
              <option value="csv">CSV</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Test Cases List */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-medium text-slate-100">
            Test Cases ({filteredTestCases.length})
          </h3>
        </div>
        
        {filteredTestCases.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-3 text-slate-500" />
            <p className="text-slate-400 text-lg">No test cases found</p>
            <p className="text-slate-400 text-sm">
              {testCases.length === 0
                ? 'Create your first test case to get started'
                : 'Try adjusting your filters'
              }
            </p>
            {testCases.length === 0 && (
              <div className="mt-4 space-x-3">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Test Case
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {filteredTestCases.map((testCase) => (
              <div key={testCase.id} className="px-6 py-4 hover:bg-slate-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getSourceTypeColor(testCase.source_type)}`}>
                        {getSourceTypeIcon(testCase.source_type)}
                        <span className="ml-1 capitalize">{testCase.source_type}</span>
                      </span>
                      
                      {testCase.category && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-slate-300 bg-slate-600">
                          <Tag className="h-3 w-3 mr-1" />
                          {testCase.category}
                        </span>
                      )}

                      {getStatusBadge(testCase.status)}
                    </div>
                    
                    <p className="text-slate-100 mb-2">{testCase.question_text}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-slate-400">
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(testCase.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Database className="h-3 w-3 mr-1" />
                        ID: {testCase.id}
                      </span>
                      {testCaseExecutions[testCase.id] > 0 && (
                        <span className="flex items-center text-orange-600">
                          <Play className="h-3 w-3 mr-1" />
                          {testCaseExecutions[testCase.id]} execution(s)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {/* Approval Controls */}
                    {testCase.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => handleApproveTestCase(testCase.id)}
                          disabled={approvingTestCases.has(testCase.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectTestCase(testCase.id)}
                          disabled={approvingTestCases.has(testCase.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Reject
                        </button>
                      </>
                    )}

                    {(testCase.status === 'APPROVED' || testCase.status === 'REJECTED') && (
                      <button
                        onClick={() => handleApproveTestCase(testCase.id)}
                        disabled={approvingTestCases.has(testCase.id)}
                        className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Approve
                      </button>
                    )}

                    <button
                      onClick={() => handleEditClick(testCase)}
                      className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleDeleteTestCase(testCase.id)}
                      disabled={testCaseExecutions[testCase.id] > 0}
                      className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        testCaseExecutions[testCase.id] > 0
                          ? 'text-gray-400 bg-slate-600 cursor-not-allowed'
                          : 'text-red-700 bg-red-100 hover:bg-red-200'
                      }`}
                      title={
                        testCaseExecutions[testCase.id] > 0
                          ? `Cannot delete: ${testCaseExecutions[testCase.id]} test execution(s) exist`
                          : 'Delete test case'
                      }
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Test Case Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Test Case</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Test Case Text *
                  </label>
                  <textarea
                    value={formData.question_text}
                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter your test case..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Source Type
                  </label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value as any })}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="manual">Manual</option>
                    <option value="pdf">PDF</option>
                    <option value="webpage">Webpage</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., factual, procedural, conceptual"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTestCase}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Create Test Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Test Case Modal */}
      {showEditModal && editingTestCase && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Test Case</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Test Case Text *
                  </label>
                  <textarea
                    value={formData.question_text}
                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter your test case..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Source Type
                  </label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value as any })}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="manual">Manual</option>
                    <option value="pdf">PDF</option>
                    <option value="webpage">Webpage</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., factual, procedural, conceptual"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TestCaseStatus })}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTestCase(null);
                    setFormData(emptyForm());
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTestCase}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Update Test Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TestCases;
